/**
 * Page de gestion des clients PWA pour les cybers
 * Permet aux cybers de voir les clients qui ont activé le suivi
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Search,
  Refresh,
  PersonAdd,
  CheckCircle,
  Schedule,
  Visibility,
  CheckCircleOutline,
  Cancel,
  NotificationsActive,
} from '@mui/icons-material';
import axios from 'axios';

interface CyberClient {
  relationship_id: number;
  client_account_id: number;
  anef_identifiant: string;
  client_nom: string;
  client_prenom: string;
  client_email: string;
  client_telephone?: string;
  client_status: string;
  client_last_login?: string;
  relationship_status: string;
  suivi_active: boolean;
  total_dossiers: number;
  dossiers_monitored: number;
  unread_events_count: number;
  subscription_status?: string;
  trial_ends_at?: string;
  is_trial?: boolean;
}

interface PendingRequest {
  relationship_id: number;
  client_id: number;
  nom: string;
  prenom: string;
  email: string;
  telephone?: string;
  anef_identifiant: string;
  total_dossiers: number;
  created_at: string;
}

interface Stats {
  total_clients: number;
  active_clients: number;
  pending_invitations: number;
  total_dossiers: number;
  total_unread_events: number;
}

export default function CyberClientsPWA() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<CyberClient[]>([]);
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [stats, setStats] = useState<Stats>({
    total_clients: 0,
    active_clients: 0,
    pending_invitations: 0,
    total_dossiers: 0,
    total_unread_events: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const [statsRes, clientsRes, pendingRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/cyber-clients/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/cyber-clients`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/cyber-clients/pending-requests`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setStats(statsRes.data.data);
      setClients(clientsRes.data.data.clients || []);
      setPendingRequests(pendingRes.data.data.pending_requests || []);
    } catch (error: any) {
      console.error('Erreur chargement données:', error);
      alert(error.response?.data?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (relationshipId: number) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/cyber-clients/accept-request/${relationshipId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      alert('✅ Client ajouté avec succès !');
      fetchData();
    } catch (error: any) {
      console.error('Erreur acceptation:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'acceptation');
    }
  };

  const handleRejectRequest = async (relationshipId: number) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/cyber-clients/reject-request/${relationshipId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      alert('Demande refusée');
      fetchData();
    } catch (error: any) {
      console.error('Erreur refus:', error);
      alert(error.response?.data?.message || 'Erreur lors du refus');
    }
  };

  const handleViewClient = (clientAccountId: number) => {
    navigate(`/cyber-clients/${clientAccountId}`);
  };

  const filteredClients = clients.filter((client) => {
    const searchLower = search.toLowerCase();
    return (
      client.client_nom.toLowerCase().includes(searchLower) ||
      client.client_prenom.toLowerCase().includes(searchLower) ||
      client.client_email.toLowerCase().includes(searchLower) ||
      client.anef_identifiant.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* En-tête */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
            📱 Mes Clients PWA
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gérez les clients du grand public qui vous ont autorisé à suivre leurs dossiers ANEF
          </Typography>
        </Box>

        {/* Statistiques */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {stats.total_clients}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total clients
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'success.50' }}>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {stats.active_clients}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Actifs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'warning.50' }}>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  {stats.pending_invitations}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  En attente
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {stats.total_dossiers}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Dossiers suivis
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card sx={{ bgcolor: 'error.50' }}>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  {stats.total_unread_events}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Événements non lus
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Barre d'actions */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher un client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>
            Actualiser
          </Button>
        </Box>

        {/* Onglets */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab
              label={
                <Badge badgeContent={filteredClients.length} color="primary">
                  <span style={{ marginRight: '8px' }}>Clients actifs</span>
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={pendingRequests.length} color="warning">
                  <span style={{ marginRight: '8px' }}>Demandes en attente</span>
                </Badge>
              }
            />
          </Tabs>
        </Box>

        {/* Contenu selon l'onglet */}
        {tabValue === 0 && (
          <>
            {filteredClients.length === 0 ? (
              <Alert severity="info">
                Aucun client PWA pour le moment. Les clients du grand public peuvent vous demander de suivre leurs dossiers.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {filteredClients.map((client) => (
                  <Grid item xs={12} sm={6} md={4} key={client.relationship_id}>
                    <Card
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 4 },
                        borderLeft: '4px solid',
                        borderColor: client.suivi_active ? 'success.main' : 'grey.300',
                      }}
                      onClick={() => handleViewClient(client.client_account_id)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'start', mb: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                              {client.client_prenom} {client.client_nom}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {client.anef_identifiant}
                            </Typography>
                          </Box>
                          {client.suivi_active ? (
                            <CheckCircle sx={{ color: 'success.main' }} />
                          ) : (
                            <Cancel sx={{ color: 'grey.400' }} />
                          )}
                        </Box>

                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          📧 {client.client_email}
                        </Typography>
                        {client.client_telephone && (
                          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                            📞 {client.client_telephone}
                          </Typography>
                        )}

                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
                          <Chip
                            size="small"
                            label={`${client.total_dossiers} dossiers`}
                            color="primary"
                            variant="outlined"
                          />
                          {client.unread_events_count > 0 && (
                            <Chip
                              size="small"
                              label={`${client.unread_events_count} nouveaux`}
                              color="error"
                              icon={<NotificationsActive />}
                            />
                          )}
                          {client.is_trial && (
                            <Chip
                              size="small"
                              label="Essai gratuit"
                              color="info"
                              variant="outlined"
                            />
                          )}
                        </Box>

                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          startIcon={<Visibility />}
                          sx={{ mt: 2 }}
                          onClick={() => handleViewClient(client.client_account_id)}
                        >
                          Voir les dossiers
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {tabValue === 1 && (
          <>
            {pendingRequests.length === 0 ? (
              <Alert severity="info">Aucune demande en attente</Alert>
            ) : (
              <Grid container spacing={2}>
                {pendingRequests.map((request) => (
                  <Grid item xs={12} sm={6} md={4} key={request.relationship_id}>
                    <Card sx={{ borderLeft: '4px solid', borderColor: 'warning.main' }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem', mb: 0.5 }}>
                          {request.prenom} {request.nom}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {request.anef_identifiant}
                        </Typography>

                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          📧 {request.email}
                        </Typography>
                        {request.telephone && (
                          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                            📞 {request.telephone}
                          </Typography>
                        )}

                        <Chip
                          size="small"
                          label={`${request.total_dossiers} dossiers`}
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 2 }}
                        />

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<CheckCircleOutline />}
                            onClick={() => handleAcceptRequest(request.relationship_id)}
                          >
                            Accepter
                          </Button>
                          <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => handleRejectRequest(request.relationship_id)}
                          >
                            Refuser
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Container>
    </Layout>
  );
}
